import { useState } from 'react';
import { Modal, Form, InputNumber, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import { generateQrStickerPdf, QrLabelSizeMm, QrUrlBuilder } from 'utils/qr/qrRenderer';
import { showError } from 'utils/notifications';

type GenerateQrModalProps = {
  open: boolean;
  onClose: () => void;
  /** Reserves `count` new codes on the backend - tools and loggers each have their own sequence there. */
  generateCodes: (count: number) => Promise<string[]>;
  /** The URL a sticker's QR encodes for a code: a phone camera opens it, the in-app scanners parse the code back out. */
  buildUrl: QrUrlBuilder;
  /** Start of the downloaded file's name, e.g. "kvalilog-tool-qr". */
  fileNamePrefix: string;
  /** Sticker sizes offered; a single size is used as is and the size choice is not shown. */
  sizes?: QrLabelSizeMm[];
};

const ALL_SIZES: QrLabelSizeMm[] = [ 40, 80 ];

const GenerateQrModal = ({ open, onClose, generateCodes, buildUrl, fileNamePrefix, sizes = ALL_SIZES }: GenerateQrModalProps) => {
  const { t } = useTranslation();

  const [ count, setCount ] = useState(6);
  const [ sizeMm, setSizeMm ] = useState<QrLabelSizeMm>(sizes.includes(80) ? 80 : sizes[0]);
  const [ isProcessing, setIsProcessing ] = useState(false);
  const [ progress, setProgress ] = useState(0);

  const handleClose = () => {
    if (isProcessing) return;
    setProgress(0);
    onClose();
  };

  const handleOk = async () => {
    setIsProcessing(true);
    setProgress(0);

    try {
      const codes = await generateCodes(count);
      const pdf = await generateQrStickerPdf(codes, sizeMm, buildUrl, setProgress);
      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${fileNamePrefix}-${date}-x${codes.length}-${sizeMm}mm.pdf`);
      handleClose();
    } catch (err: any) {
      showError(t('components.generateqr.failed'), err?.data?.Message || err?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      title={t('components.generateqr.title')}
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={isProcessing}
      cancelButtonProps={{ disabled: isProcessing }}
      okText={isProcessing ? t('components.generateqr.generating', { progress }) : t('components.generateqr.generate')}
      maskClosable={!isProcessing}
      closable={!isProcessing}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label={t('components.generateqr.count')}>
          <InputNumber
            min={1}
            max={500}
            value={count}
            onChange={(value) => setCount(Number(value) || 1)}
            disabled={isProcessing}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {sizes.length > 1 && (
          <Form.Item label={t('components.generateqr.size')}>
            <Radio.Group
              value={sizeMm}
              onChange={(e) => setSizeMm(e.target.value)}
              disabled={isProcessing}
              optionType="button"
              buttonStyle="solid"
            >
              {sizes.map((size) => (
                <Radio.Button key={size} value={size}>{`${size / 10} x ${size / 10} cm`}</Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default GenerateQrModal;
